import { useState, useCallback } from 'react'
import OwnerBio from '#/components/about/OwnerBio'
import TeamGrid from '#/components/about/TeamGrid'
import Container from '#/components/layout/Container'
import Section from '#/components/layout/Section'
import { useCmsContent } from '#/hooks/useCmsContent'
import { useCms } from '#/components/cms/CmsProvider'
import EditModal from '#/components/cms/EditModal'
import EditablePageHeader from '#/components/cms/EditablePageHeader'
import type { EditField } from '#/components/cms/EditModal'
import { addPendingAdd } from '#/lib/cms-store'
import { getTeamMembers } from '#/lib/content'
import { createFileRoute } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { seoMeta, canonicalLink } from '#/lib/seo'

export const Route = createFileRoute('/about')({
  head: () => ({
    meta: seoMeta({
      title: 'About Us',
      description:
        'Meet the owners and team behind Main Street Bread — three generations of bakers bringing artisan bread and community to Ferndale, MI.',
      path: '/about',
    }),
    links: [canonicalLink('/about')],
  }),
  loader: async () => {
    const members = await getTeamMembers()
    return { members }
  },
  component: AboutPage,
})

const ownerFields: EditField[] = [
  { name: 'name', type: 'text', label: 'Name' },
  { name: 'role', type: 'text', label: 'Role' },
  { name: 'bio', type: 'textarea', label: 'Bio' },
  { name: 'image', type: 'image', label: 'Image' },
]

const teamFields: EditField[] = [
  { name: 'name', type: 'text', label: 'Name' },
  { name: 'role', type: 'text', label: 'Role' },
  { name: 'image', type: 'image', label: 'Image' },
]

function AboutPage() {
  const data = Route.useLoaderData()
  const members = useCmsContent('team-members', data.members)
  const { isEditMode } = useCms()
  const [addOwnerOpen, setAddOwnerOpen] = useState(false)
  const [addTeamOpen, setAddTeamOpen] = useState(false)

  const owners = members
    .filter((m) => m.isOwner)
    .sort((a, b) => a.order - b.order)
  const team = members
    .filter((m) => !m.isOwner)
    .sort((a, b) => a.order - b.order)

  const handleAddOwner = useCallback((values: Record<string, unknown>) => {
    const id = String(values.name ?? 'owner')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
    addPendingAdd('team-members', {
      id: `owner-${id}-${Date.now()}`,
      name: values.name ?? '',
      role: values.role ?? '',
      bio: values.bio ?? '',
      image: values.image ?? '',
      isOwner: true,
      order: owners.length,
    })
  }, [owners.length])

  const handleAddTeam = useCallback((values: Record<string, unknown>) => {
    const id = String(values.name ?? 'member')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
    addPendingAdd('team-members', {
      id: `member-${id}-${Date.now()}`,
      name: values.name ?? '',
      role: values.role ?? '',
      bio: '',
      image: values.image ?? '',
      isOwner: false,
      order: team.length,
    })
  }, [team.length])

  return (
    <main>
      {/* Owners */}
      <Section tone="surface">
        <Container>
          <EditablePageHeader page="about" className="mb-12 sm:mb-16" />

          <div className="space-y-16">
            {owners.map((owner, i) => (
              <OwnerBio key={owner.id} owner={owner} reverse={i % 2 === 1} />
            ))}
          </div>

          {isEditMode && (
            <>
              <button
                onClick={() => setAddOwnerOpen(true)}
                className="mx-auto mt-10 flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-md transition-transform hover:scale-105"
              >
                <Plus className="size-4" />
                Add Owner
              </button>
              <EditModal
                open={addOwnerOpen}
                onOpenChange={setAddOwnerOpen}
                title="Add Owner"
                fields={ownerFields}
                values={{ name: '', role: '', bio: '', image: '' }}
                onSave={handleAddOwner}
              />
            </>
          )}
        </Container>
      </Section>

      {/* Decorative pull quote */}
      <Section tone="base" className="py-10 sm:py-16">
        <Container className="max-w-3xl text-center">
          <div className="mx-auto mb-4 h-px w-16 bg-gradient-to-r from-transparent via-primary-container to-transparent" />
          <blockquote className="font-serif text-xl italic leading-relaxed text-on-surface sm:text-2xl">
            &ldquo;Good bread takes time. So does building something meaningful.&rdquo;
          </blockquote>
          {/* <p className="mt-3 text-sm text-on-surface-variant">— Maria Santos, Co-Owner</p> */}
          <div className="mx-auto mt-4 h-px w-16 bg-gradient-to-r from-transparent via-primary-container to-transparent" />
        </Container>
      </Section>

      {/* Team */}
      <Section tone="low">
        <Container>
          <div className="mb-10 text-center sm:mb-14">
            <p className="kicker mb-2">The Crew</p>
            <h2 className="font-serif text-3xl font-bold tracking-tight text-on-surface sm:text-4xl">
              Meet the Team
            </h2>
          </div>
          <TeamGrid members={team} />

          {isEditMode && (
            <>
              <button
                onClick={() => setAddTeamOpen(true)}
                className="mx-auto mt-10 flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-md transition-transform hover:scale-105"
              >
                <Plus className="size-4" />
                Add Team Member
              </button>
              <EditModal
                open={addTeamOpen}
                onOpenChange={setAddTeamOpen}
                title="Add Team Member"
                fields={teamFields}
                values={{ name: '', role: '', image: '' }}
                onSave={handleAddTeam}
              />
            </>
          )}
        </Container>
      </Section>
    </main>
  )
}
